import { ConfirmationAlert, FormInputRow, ImageEdit, ImageForm, ImageUploader, ImageUtil } from '@emerald-works-nova/components'
import { Divider, Grid, Typography } from '@mui/material'
import { isEqual } from 'lodash'
import React from 'react'
import { useForm } from 'react-hook-form'

const Image = ({ form, imgPrefix, isReadOnly }) => {
  const formImage = form.getValues().image

  const { formState: { isDirty }, setValue, watch, ...formThumbnail } = useForm({
    defaultValues: {
      ...formImage
    }
  })

  const [thumbEditOpen, setThumbEditOpen] = React.useState(false)
  const [showRemoveAlert, setShowRemoveAlert] = React.useState(false)
  const imgInfo = watch()

  const parseImgixUrl = React.useCallback((s3Url, imgixParams = {}) => {
    return ImageUtil.ImgixGenerateImageUrl(s3Url, { ...imgixParams, w: 425, h: 285 })
  }, [])

  const [imgPreviewUrl, setImgPreviewUrl] = React.useState(parseImgixUrl(formImage?.s3Url, formImage?.imgixCropParams))

  // Update form state with content
  React.useEffect(() => {
    // Only set the form state once
    // If content is present and form has no change
    const { image } = form.getValues() || {}
    if (image?.name && !isDirty) {
      Object.entries(image).forEach(([name, value]) => setValue(name, value))
    }
  }, [form, isDirty, setValue])

  const handleOnDropAccepted = React.useCallback(imgInfo => {
    Object.entries(imgInfo).forEach(([name, value]) => setValue(name, value))
    setImgPreviewUrl(parseImgixUrl(imgInfo.s3Url))
  }, [setValue, parseImgixUrl])

  const handleCropDone = React.useCallback(cropInfo => {
    Object.entries(cropInfo).forEach(([name, value]) => setValue(name, value))
    const newImgPreviewUrl = parseImgixUrl(imgInfo.s3Url, cropInfo.imgixCropParams)
    setImgPreviewUrl(newImgPreviewUrl)
  }, [setValue, imgInfo.s3Url, parseImgixUrl])

  const handleCloseCropModal = React.useCallback(() => setThumbEditOpen(false), [])

  // watch form changes to trigger auto save
  // is form has changes and data is present
  // trigger auto save
  watch(values => {
    const hasChanges = !isEqual(form.getValues('image'), values)
    if (hasChanges) {
      form.setValue('image', values, { shouldDirty: true })
      form.trigger('image')
    }
  })

  const handleRemoveImage = React.useCallback(async () => {
    formThumbnail.reset({})
    setShowRemoveAlert(false)
  }, [formThumbnail])

  return (
    <>
      <ImageEdit open={thumbEditOpen} image={imgInfo} onClose={handleCloseCropModal} onCropDone={handleCropDone} isReadOnly={isReadOnly} />
      <ConfirmationAlert
        title='Deleting image?'
        description={"Deleting the image will also remove all the information added, and it won't be possible to restore it later."}
        agreeText='Delete'
        showModal={showRemoveAlert}
        onConfirmation={handleRemoveImage}
        onCancel={() => setShowRemoveAlert(false)}
        disableBackdropClick
      />
      <Grid container direction='column'>
        <Typography variant='h6' tabIndex={0}>Image details</Typography>
        <Divider sx={{ marginTop: 3 }} />
        {!imgInfo?.imgUrl ? (
          // Image Upload Page
          <FormInputRow
            hideTitle
            description={`The thumbnail will represent the content visually to the end-user, 
            and is a requirement for this content to be ready for publication.`}
            InputComponent={
              <ImageUploader
                data-test='skill-bite-image-uploader'
                onDropAccepted={handleOnDropAccepted}
                imageNamePrefix={imgPrefix}
              />
            }
          />
        ) : (
          // Image editing page
          <ImageForm
            form={formThumbnail}
            imgInfo={imgInfo}
            onEditImage={setThumbEditOpen}
            onRemoveImage={setShowRemoveAlert}
            imgUrl={imgPreviewUrl}
            isReadOnly={isReadOnly}
          />
        )}
      </Grid>
    </>
  )
}

export default Image
